import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import WaveSurfer from 'wavesurfer.js';
import WavesurferPlayer from '@wavesurfer/react';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm';
import { Pencil, InfoCircle } from '@saleshandy/icons';
import { Button } from '@saleshandy/design-system';
import Modal from '../../../../shared/design-system/ui/modal';
import Input from '../../../../shared/design-system/components/input';
import Checkbox from '../../../../shared/design-system/components/atoms/checkbox';
import { setShowMicrophoneAccessWarningModal } from '../../dialer-slice';
import { RecordVoicemailProps } from './type';
import { accessibleOnClick } from '../../../../shared/utils';
import {
  PauseIcon,
  PlayAudioIcon,
  MusicFileIcon,
  Cross,
  VoiceMailIcon,
} from '../../../../shared/svg';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import toaster, { Theme } from '../../../../shared/toaster';
import Select from '../../../../shared/design-system/components/select';
import { getViewModalHeaderTitle } from '../../../prospect/helpers/draggable-input/helper';
import {
  TRANSCRIPTION_LANGUAGES,
  DEFAULT_TRANSCRIPTION_LANGUAGE,
} from '../../constants/transcription-languages';
import GlobalRestrictionModal from '../../../home/components/global-restriction-modal';

const MAX_RECORDING_TIME = 60000; // 30 seconds in milliseconds
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_DURATION_SEC = 60;
const MAX_FILE_NAME_LENGTH = 50;

const getAudioDurationFromBlob = (blob: Blob): Promise<number> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      resolve(duration);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load audio'));
    };
    audio.src = url;
  });

const RecordVoicemail: React.FC<RecordVoicemailProps> = ({
  show,
  onClose,
  onSave,
  fromSettings = false,
  defaultTitle,
  initialAudioUrl,
  existingAudioFileKey,
  defaultDuration,
  editMode = false,
  defaultLanguage = DEFAULT_TRANSCRIPTION_LANGUAGE,
  hideLanguageSelection = false,
  isDemoAccount,
}) => {
  // ** Refs
  const micWaveformRef = useRef<HTMLDivElement | null>(null);
  const recordPluginRef = useRef<any | null>(null); // Start recording

  const wavesurferRef = useRef<WaveSurfer | null>(null); // Record waveform

  const wavesurferPlayerRef = useRef<WaveSurfer | null>(null); // Play/Pause
  const isInitialMountRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const dispatch = useDispatch();

  // ** local state
  const [audioURL, setAudioURL] = useState<string | null>(
    initialAudioUrl || null,
  );
  const [audioBlob, setAudioBlob] = useState<Blob | null>(
    existingAudioFileKey ? new Blob([existingAudioFileKey]) : null,
  );
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(
    defaultTitle || null,
  );
  const [uploadedFileSize, setUploadedFileSize] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState<number>(
    (defaultDuration || 0) * 1000,
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState<number>(0);
  const [isMarkAsDefault, setIsMarkAsDefault] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [editedTitle, setEditedTitle] = useState<string>(
    defaultTitle || getViewModalHeaderTitle(),
  );
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  // const [showDefaultAudio, setShowDefaultAudio] = useState<boolean>(false);
  const [viewNameError, setViewNameError] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    defaultLanguage,
  );
  // True when user records, uploads, or removes — false when only title changed
  const [hasAudioChanged, setHasAudioChanged] = useState(false);

  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  const createWaveSurfer = useCallback(() => {
    if (!micWaveformRef.current) return;

    wavesurferRef.current?.destroy();

    const ws = WaveSurfer.create({
      container: micWaveformRef.current,
      waveColor: '#BFDBFE',
      progressColor: '#2563EB',
      width: 286,
      height: 24,
      cursorWidth: 0,
      barWidth: 2,
      barGap: 3,
      barRadius: 13,
      barHeight: 1,
      normalize: false,
      autoScroll: false,
    });

    const record = ws.registerPlugin(
      RecordPlugin.create({
        renderRecordedAudio: false,
        scrollingWaveform: true,
        continuousWaveform: false,
        continuousWaveformDuration: 30,
      }),
    );

    record.on('record-end', (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      setAudioURL(url);
      setAudioBlob(blob);
      setUploadedFileName(null); // Clear uploaded file name when recording
      setUploadedFileSize(null);
      setRecordingTime(0);
      setIsPlaying(false);
      setPlaybackTime(0);
      setHasAudioChanged(true);
    });

    record.on('record-progress', (time: number) => {
      setRecordingTime(time);
      // Automatically stop recording when 30 seconds limit is reached
      if (time >= MAX_RECORDING_TIME) {
        record.stopRecording();
        setIsRecording(false);
      }
    });

    wavesurferRef.current = ws;
    recordPluginRef.current = record;
  }, []);

  const formatTime = useCallback((time: number): string => {
    // time will be in milliseconds, convert it to mm:ss format
    const formattedTime = [
      Math.floor((time % 3600000) / 60000), // minutes
      Math.floor((time % 60000) / 1000), // seconds
    ]
      .map((v) => String(v).padStart(2, '0'))
      .join(':');
    return formattedTime;
  }, []);

  const formatFileSize = useCallback((size: number): string => {
    const sizeInMb = size / 1024 / 1024;
    if (sizeInMb >= 1) {
      return `${sizeInMb.toFixed(1)}MB`;
    }
    const sizeInKb = size / 1024;
    return `${sizeInKb.toFixed(1)}KB`;
  }, []);

  const truncateFileName = useCallback(
    (fileName: string, maxLength = 20): string => {
      if (fileName.length <= maxLength) {
        return fileName;
      }
      const extension = fileName.substring(fileName.lastIndexOf('.'));
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      const truncatedName = nameWithoutExt.substring(
        0,
        maxLength - extension.length - 3,
      );
      return `${truncatedName}..${extension}`;
    },
    [],
  );

  const toggleRecording = async () => {
    let record = recordPluginRef.current;

    // Lazy init: after Remove, the effect may not have run yet; recreate on demand
    if (!record && micWaveformRef.current) {
      createWaveSurfer();
      record = recordPluginRef.current;
    }
    if (!record) return;

    if (record.isRecording() || record.isPaused()) {
      record.stopRecording();
      setIsRecording(false);
      return;
    }

    // Check microphone permission before starting
    try {
      const stream = await navigator?.mediaDevices?.getUserMedia({
        audio: true,
      });
      stream?.getTracks()?.forEach((track) => track?.stop());
    } catch {
      dispatch(setShowMicrophoneAccessWarningModal(true));
      return;
    }

    setRecordingTime(0);
    await record.startRecording({ deviceId: 'default' });
    setIsRecording(true);
  };

  const handleRemove = useCallback(() => {
    // Clean up the audio URL to prevent memory leaks
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }

    // Destroy the wavesurfer player if it exists
    wavesurferPlayerRef.current?.destroy();
    wavesurferPlayerRef.current = null;

    // Destroy the recording wavesurfer
    wavesurferRef.current?.destroy();
    wavesurferRef.current = null;
    recordPluginRef.current = null;

    // Reset state
    setAudioURL(null);
    setAudioBlob(null);
    setUploadedFileName(null);
    setEditedTitle(defaultTitle || getViewModalHeaderTitle());
    setUploadedFileSize(null);
    setIsRecording(false);
    setRecordingTime(0);
    setIsPlaying(false);
    setPlaybackTime(0);
    setHasAudioChanged(true);
    setIsEditingTitle(false);
  }, [audioURL, defaultTitle]);

  // Do not call stopRecording() here: that finalizes the take and fires record-end,
  // which can repopulate state after cleanup (e.g. ESC while recording). Destroy via handleRemove instead.
  const handleClose = useCallback(() => {
    handleRemove();
    setViewNameError(false);
    onClose();
  }, [onClose, handleRemove]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDownloadFile = useCallback(async () => {
    const fileName =
      uploadedFileName || editedTitle || defaultTitle || 'voicemail.mp3';

    // Case 1: Real audio blob (from recording or upload)
    if (audioBlob && audioBlob.size > 100) {
      const url = URL.createObjectURL(audioBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      return;
    }

    // Case 2: Edit mode – audio at signed URL (audioURL is https)
    if (audioURL && audioURL.startsWith('http')) {
      try {
        const response = await fetch(audioURL);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        toaster.error('Failed to download audio.');
      }
    }
  }, [audioBlob, audioURL, uploadedFileName, editedTitle, defaultTitle]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validate file type
        const validTypes = [
          'audio/mp3',
          'audio/mpeg',
          'audio/wav',
          'audio/wave',
        ];
        const validExtensions = ['.mp3', '.wav'];
        const fileExtension = file.name
          .substring(file.name.lastIndexOf('.'))
          .toLowerCase();

        if (
          validTypes.includes(file.type) ||
          validExtensions.includes(fileExtension)
        ) {
          // Clean up previous audio URL if exists
          if (audioURL) {
            URL.revokeObjectURL(audioURL);
          }

          // Create new audio URL from the file
          const url = URL.createObjectURL(file);
          setAudioURL(url);
          setAudioBlob(file);
          setUploadedFileName(file?.name);
          setEditedTitle(file?.name);
          setUploadedFileSize(file.size);
          setIsRecording(false);
          setRecordingTime(0);
          setIsPlaying(false);
          setPlaybackTime(0);
          setHasAudioChanged(true);
        }
      }
      // Reset input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [audioURL],
  );

  // Initialize WaveSurfer when modal is shown and DOM is ready
  useEffect(() => {
    if (!show) {
      // Clean up when modal is closed
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
        recordPluginRef.current = null;
      }
      return undefined;
    }

    // Use setTimeout to ensure modal DOM is fully rendered
    const timer = setTimeout(() => {
      if (micWaveformRef.current && !wavesurferRef.current) {
        createWaveSurfer();
        isInitialMountRef.current = false;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [show, createWaveSurfer]);

  // Recreate wavesurfer when audioURL becomes null (after remove)
  useEffect(() => {
    // Skip on initial mount (handled by the useEffect above)
    if (isInitialMountRef.current) {
      return undefined;
    }

    if (!audioURL && !wavesurferRef.current && show) {
      // Use setTimeout to ensure DOM is updated after state change
      const timer = setTimeout(() => {
        if (micWaveformRef.current) {
          createWaveSurfer();
        }
      }, 0);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [audioURL, createWaveSurfer, show]);

  // Computed conditional className
  const recNotStartedClassName =
    !audioURL && !isRecording ? 'rec-not-started' : '';
  const handleSave = useCallback(async () => {
    if (isDemoAccount) {
      setShowGlobalRestrictionModal(true);
      return;
    }
    const fileName = editedTitle?.trim() || `voicemail-${Date.now()}.mp3`;
    if (fileName.length > MAX_FILE_NAME_LENGTH) {
      toaster.error('File name must be 50 characters or less.', {
        theme: Theme.New,
      });
      return;
    }
    // Title-only update (edit mode, user didn't change audio)
    const isTitleOnlyUpdate = editMode && !hasAudioChanged && audioURL;
    if (isTitleOnlyUpdate) {
      try {
        setIsSaving(true);
        const durationSec = defaultDuration || 0;
        await onSave(
          null,
          fileName,
          0,
          durationSec,
          'update',
          existingAudioFileKey,
          DEFAULT_TRANSCRIPTION_LANGUAGE,
        );
        handleRemove();
        onClose();
      } catch (error) {
        toaster.error('Failed to save voicemail.', { theme: Theme.New });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Create or update with new audio
    if (!audioBlob) return;

    const fileSize = uploadedFileSize ?? audioBlob.size;
    if (fileSize > MAX_FILE_SIZE_BYTES) {
      toaster.error('File size must be 5MB or less.', { theme: Theme.New });
      return;
    }

    try {
      setIsSaving(true);
      let durationSec =
        Math.ceil(wavesurferPlayerRef?.current?.getDuration?.() || 0) ?? 0;
      if (durationSec <= 0) {
        durationSec = await getAudioDurationFromBlob(audioBlob);
      }
      if (durationSec > MAX_DURATION_SEC) {
        toaster.error('Audio duration must be 60 seconds or less.', {
          theme: Theme.New,
        });
        return;
      }
      await onSave(
        audioBlob,
        fileName,
        fileSize,
        Number(durationSec),
        editMode ? 'update' : 'create',
        undefined,
        DEFAULT_TRANSCRIPTION_LANGUAGE,
      );
      handleRemove();
      onClose();
    } catch (error) {
      toaster.error('Failed to save voicemail.', { theme: Theme.New });
    } finally {
      setIsSaving(false);
    }
  }, [
    audioBlob,
    onSave,
    editedTitle,
    uploadedFileSize,
    onClose,
    handleRemove,
    editMode,
    hasAudioChanged,
    existingAudioFileKey,
    audioURL,
    defaultDuration,
    selectedLanguage,
  ]);

  const onSaveViewName = () => {
    if (viewNameError) return;
    setIsEditingTitle(false);
    if (editedTitle.trim() === '') {
      setEditedTitle(getViewModalHeaderTitle());
    }
  };

  useEffect(() => {
    if (show && !editMode) {
      setSelectedLanguage(defaultLanguage || DEFAULT_TRANSCRIPTION_LANGUAGE);
    }
  }, [show]);

  // Sync state with props when modal opens in edit mode. Do NOT include audioURL
  // in deps — when user clicks Remove we set audioURL to null; including it would
  // re-run this effect and restore the audio from initialAudioUrl.
  useEffect(() => {
    if (editMode && !hasAudioChanged) {
      setEditedTitle(defaultTitle);
      setAudioURL(initialAudioUrl ?? null);
      setAudioBlob(
        existingAudioFileKey ? new Blob([existingAudioFileKey]) : null,
      );
      setRecordingTime((defaultDuration || 0) * 1000);
      setSelectedLanguage(defaultLanguage || DEFAULT_TRANSCRIPTION_LANGUAGE);
    }
  }, [
    editMode,
    existingAudioFileKey,
    defaultDuration,
    defaultTitle,
    initialAudioUrl,
    hasAudioChanged,
    defaultLanguage,
  ]);

  // Create flow: component can stay mounted while hidden — refresh title time before paint on each open.
  useLayoutEffect(() => {
    if (!show || editMode) return;
    setEditedTitle(
      defaultTitle?.trim() ? defaultTitle : getViewModalHeaderTitle(),
    );
  }, [show, editMode, defaultTitle]);

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    wavesurferPlayerRef.current?.destroy();
    wavesurferPlayerRef.current = null;

    setAudioURL((prev) => {
      if (prev?.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setAudioBlob(null);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    setIsRecording(false);
    setRecordingTime(0);
    setIsPlaying(false);
    setPlaybackTime(0);
    setIsMarkAsDefault(false);
    setIsSaving(false);
    setIsEditingTitle(false);
    setEditedTitle(getViewModalHeaderTitle());
    setViewNameError(false);
    setSelectedLanguage(defaultLanguage || DEFAULT_TRANSCRIPTION_LANGUAGE);
    setHasAudioChanged(false);
  }, [show, defaultLanguage]);

  return (
    <>
      <Modal
        title={
          <div className="d-flex flex-column">
            <div
              className="categorize-by-modal-header-container"
              onDoubleClick={() => setIsEditingTitle(true)}
            >
              <VoiceMailIcon width={24} height={24} />
              {isEditingTitle ? (
                <Input
                  type="text"
                  value={editedTitle}
                  onChange={(value: string) => {
                    setEditedTitle(value);
                    if (value?.length > 50) {
                      setViewNameError(true);
                    } else {
                      setViewNameError(false);
                    }
                  }}
                  onBlur={() => {
                    onSaveViewName();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onSaveViewName();
                    }
                  }}
                  className={
                    viewNameError
                      ? 'input-error categorize-by-modal__title-input'
                      : 'categorize-by-modal__title-input'
                  }
                  autoFocus
                />
              ) : (
                <TruncatedTextWithAutoTooltip
                  text={editedTitle}
                  className="categorize-by-modal-truncatedText"
                />
              )}
              {!isEditingTitle && (
                <OverlayTooltip text="Edit voicemail name">
                  <div
                    className="pencil-icon-container"
                    {...accessibleOnClick(() => {
                      setIsEditingTitle(true);
                    })}
                  >
                    <Pencil width={16} height={16} />
                  </div>
                </OverlayTooltip>
              )}
            </div>
            {viewNameError && (
              <div className="error-text">
                Voicemail title should be less than or equal to 50 characters
              </div>
            )}
          </div>
        }
        show={show}
        onHide={handleClose}
        className="record-voicemail-popup"
        primaryBtnLabel={editMode ? 'Update Voicemail' : 'Save Voicemail'}
        primaryBtnClickedHandler={handleSave}
        primaryBtnLoading={isSaving}
        secondaryBtnClickedHandler={handleClose}
        secondaryBtnDisabled={isRecording}
        primaryBtnDisabled={!audioURL || isSaving || viewNameError}
      >
        <div className="record-voicemail-popup-container">
          <div className="record-voicemail-popup-container-content">
            <div className="record-section">
              <div className={`record-waveform ${recNotStartedClassName}`}>
                {audioURL ? (
                  <>
                    <span
                      className="record-actions"
                      {...accessibleOnClick(() =>
                        wavesurferPlayerRef.current?.playPause(),
                      )}
                    >
                      {isPlaying ? (
                        <PauseIcon
                          width={16}
                          height={16}
                          className={`record-actions-icon-pause ${
                            isPlaying ? 'active' : ''
                          }`}
                        />
                      ) : (
                        <PlayAudioIcon
                          width={16}
                          height={16}
                          className="record-actions-icon-play"
                        />
                      )}
                    </span>
                    <span className="recording-player">
                      <WavesurferPlayer
                        width={262}
                        height={24}
                        url={audioURL}
                        waveColor="#BFDBFE"
                        progressColor="#1D4ED8"
                        cursorWidth={0}
                        barWidth={3}
                        barGap={2}
                        barRadius={13}
                        barHeight={1}
                        normalize={false}
                        autoScroll={true}
                        onReady={(ws) => {
                          wavesurferPlayerRef.current = ws as WaveSurfer;

                          // Listen to play/pause events
                          ws.on('play', () => {
                            setIsPlaying(true);
                          });

                          ws.on('pause', () => {
                            setIsPlaying(false);
                          });

                          ws.on('finish', () => {
                            setIsPlaying(false);
                            setPlaybackTime(0);
                          });

                          // Listen to timeupdate to track playback progress
                          ws.on('timeupdate', (currentTime: number) => {
                            // currentTime is in seconds, convert to milliseconds
                            setPlaybackTime(currentTime * 1000);
                          });
                        }}
                      />
                    </span>
                    {audioURL && !isRecording && (
                      <div className="recording-time">
                        {formatTime(playbackTime)}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div
                      id="mic-waveform"
                      className={`mic-waveform-style ${
                        recNotStartedClassName ? 'rec-divider' : ''
                      }`}
                      ref={micWaveformRef}
                    />
                    <div className="recording-time">
                      {formatTime(recordingTime)}
                    </div>
                  </>
                )}
              </div>
              <div className="record-controls">
                {audioURL && !isRecording ? (
                  <Button variant="secondary" onClick={handleRemove}>
                    Remove
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={toggleRecording}>
                    {isRecording ? 'Stop Recording' : 'Start Recording'}
                  </Button>
                )}
              </div>
            </div>
            {uploadedFileName ? (
              <div className="uploaded-file-section">
                <MusicFileIcon width={16} height={16} />
                <OverlayTooltip text="Download">
                  <span
                    className="uploaded-file-name"
                    {...accessibleOnClick(handleDownloadFile)}
                  >
                    {truncateFileName(uploadedFileName)}
                    {uploadedFileSize &&
                      ` (${formatFileSize(uploadedFileSize)})`}
                  </span>
                </OverlayTooltip>
                <button
                  className="uploaded-file-close"
                  {...accessibleOnClick(handleRemove)}
                  type="button"
                >
                  <Cross width={14} height={14} color="#6B7280" />
                </button>
              </div>
            ) : (
              <div
                className={`upload-section ${
                  !recNotStartedClassName ? 'upload-section-disabled' : ''
                }`}
              >
                <span className="upload-or-text">or</span>
                <span
                  className="upload-audio-link"
                  {...accessibleOnClick(handleUploadClick)}
                >
                  Upload Audio File
                </span>
                <span className="upload-format-text">(mp3, wav format)</span>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="audio/mp3,audio/mpeg,audio/wav,audio/wave,.mp3,.wav"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            {!fromSettings && (
              <div
                className={`mark-as-default ${
                  !audioURL ? 'mark-as-default-disabled' : ''
                }`}
              >
                <Checkbox
                  checked={isMarkAsDefault}
                  onChange={(checked) => setIsMarkAsDefault(checked)}
                  label="Mark as default"
                  className="mark-as-default-checkbox"
                  disabled={!audioURL}
                />
                <p className="mark-as-default-description">
                  This recording will be set as default pre-recording while
                  leaving a voice mail.
                </p>
              </div>
            )}
            {false && fromSettings && !hideLanguageSelection && audioURL && (
              <div className="record-voicemail-popup__language-section">
                <p className="record-voicemail-popup__language-label">
                  Spoken Language
                  <OverlayTooltip
                    text="Helps us accurately transcribe the audio. Transcription will be in the same language as spoken."
                    placement={Placement.Top}
                  >
                    <span className="record-voicemail-popup__language-info-icon">
                      <InfoCircle width={14} height={14} />
                    </span>
                  </OverlayTooltip>
                </p>
                {editMode && !hasAudioChanged ? (
                  <div className="record-voicemail-popup__language-display">
                    {TRANSCRIPTION_LANGUAGES.find(
                      (l) => l.key === selectedLanguage,
                    )?.value || 'Select Language'}
                  </div>
                ) : (
                  <Select<{ key: string; value: string }>
                    className="record-voicemail-popup__language-select"
                    options={TRANSCRIPTION_LANGUAGES}
                    selectedOptionKey={selectedLanguage}
                    selectedOptionRenderer={([option]) => (
                      <span>{option?.value || 'Select Language'}</span>
                    )}
                    optionRenderer={(option) => <span>{option?.value}</span>}
                    onChange={([option]) => {
                      if (option) {
                        setSelectedLanguage(option.key);
                      }
                    }}
                    placeholder="Select Language"
                    placement={Placement.Top}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title={`${
          editMode ? 'Updating' : 'Saving'
        } voicemail is disabled in demo mode`}
        content={`This is a demo account with sample data. Signup to ${
          editMode ? 'update' : 'save'
        } voicemail`}
      />
    </>
  );
};

export default memo(RecordVoicemail);
